import * as THREE from 'three';
import type { HexTileMapKey } from '@/game/hexmap';
import { axialToWorld } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import { type UiStore, useUi } from './ui/store';

const RADIUS = config.hexMap.tile.radius * 0.96;
// Flat hex outline; thetaStart π/2 puts a vertex on ±Z to match the pointy-top tiles.
const ringGeometry = new THREE.RingGeometry(RADIUS * 0.86, RADIUS, 6, 1, Math.PI / 2);
ringGeometry.rotateX(-Math.PI / 2);
const ringMaterial = new THREE.MeshBasicMaterial({
  color: 'white',
  transparent: true,
  opacity: 0.55,
  depthWrite: false,
});
// Placement mode: whether a site can go on the hovered tile. Colours mirror the
// --color-ok / --color-danger tokens in index.css (three can't read CSS tokens).
const okMaterial = new THREE.MeshBasicMaterial({
  color: '#6ee7b7',
  transparent: true,
  opacity: 0.9,
  depthWrite: false,
});
const dangerMaterial = new THREE.MeshBasicMaterial({
  color: '#fca5a5',
  transparent: true,
  opacity: 0.9,
  depthWrite: false,
});
const noRaycast = () => null;

/**
 * Outline on the hex under the cursor, so it's clear which tile the info
 * panel describes. With `allowed` (placement or tile targeting) it's ok or
 * danger coloured by whether the tile is in it. Subscribes to the hovered
 * tile itself, so hovering re-renders only this.
 */
export function HoverHighlight({
  ui,
  allowed = null,
}: {
  ui: UiStore;
  allowed?: ReadonlySet<HexTileMapKey> | null;
}) {
  const hoveredKey = useUi(ui, (s) => s.hoveredKey);
  if (!hoveredKey) return null;
  const placeable = allowed ? allowed.has(hoveredKey) : null;
  const [q, r] = hoveredKey.split(',').map(Number);
  const world = axialToWorld({ q, r }, config.hexMap.tile.radius);
  return (
    <mesh
      geometry={ringGeometry}
      material={placeable === null ? ringMaterial : placeable ? okMaterial : dangerMaterial}
      position={[world.x, config.hexMap.tile.thickness + 0.015, world.z]}
      raycast={noRaycast}
    />
  );
}
